import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'
import { useBsvExchangeRate, formatUsdValue, satsToUsd, usdToSats } from 'api/useBsvExchangeRate'
import api from 'api/api'
import { BsvValueSignals } from 'components/answers/AnswerButtons'
import SatsIcon from 'components/shared/SatsIcon'
import UserIcon from 'components/users/UserIcon'
import UpvalueGlyph from 'components/votes/UpvalueGlyph'
import { DEFAULT_UPVALUE_EMOJI } from 'components/votes/upvalueEmoji'
import { getRecentReactionEmojis } from 'components/votes/recentReactions'
import {
    getCuratedReactionOption,
    searchReactionOptions,
    type ReactionOption,
} from 'components/votes/reactions'
import { savedPaidSignalType } from 'components/votes/signalOwnership'
import { LARGE_UPVALUE_CONFIRM_MODAL_KEY } from 'components/votes/upvalueModalKeys'
import { useUpvalueAction } from 'components/votes/useUpvalueAction'
import {
    ORDINAL_CHAT_SATS_PRESETS,
    ORDINAL_CHAT_USD_PRESETS,
    buildOrdinalChatReactionOptions,
    createOrdinalChatAmountSession,
    ordinalChatSeedGradient,
    ordinalChatViewerHasSeed,
    projectedOrdinalChatSeedShare,
    summarizeOrdinalChatSignals,
} from 'lib/ordinalChatUpvalue'
import { resolveOrdinalChatPopoverPosition } from 'lib/ordinalChat'
import { SATOSHI_UNIT, SATS_BEFORE_CONFIRM, USD_UNIT, toBitcoin } from 'lib/bsv-util'
import { paidSignalDefaultOptions } from 'lib/paidSignalDefaults'
import { seedMinimumSats, useSeedingContextForAnswer } from 'lib/seeding'
import {
    ChevronDown,
    ChevronUp,
    GitBranch,
    Loader2,
    Pencil,
    Reply,
    Search,
    SmilePlus,
    Sprout,
    Star,
    X,
} from 'lucide-react'
import useStore from 'state/knovStore'
import { answerWithSignalQuestSettings } from 'state/freeReactionEligibility'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'
import styles from './ordinal-chat-upvalue-control.module.scss'

export type OrdinalChatUpvalueControlProps = {
    answer: Answer
    answers?: Answer[]
    signalQuest?: Quest
    onOpenChange?: (open: boolean) => void
    onReactionCommitted?: () => void
    onReactionSelected?: (emoji: string) => void
    onReply?: () => void
    onBranch?: () => void
    branchCount?: number
    controlledOpen?: boolean
    modalOnly?: boolean
    onRequestClose?: () => void
    actionSource?: string
    initialSelectedEmoji?: string | null
}

const compactNumber = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
})

const resolveReactionOption = (emoji: string): ReactionOption =>
    getCuratedReactionOption(emoji) || { emoji, label: emoji }

const normalizeUsd = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null

type AmountPreset = { label: string; sats: number | null; usd: number | null }

const usdAmountsEqual = (left: number | null, right: number | null) =>
    left == null || right == null
        ? left == null && right == null
        : Math.abs(left - right) < 0.000000001

export default function OrdinalChatUpvalueControl({
    answer,
    answers,
    signalQuest,
    onOpenChange,
    onReactionCommitted,
    onReactionSelected,
    onReply,
    onBranch,
    branchCount = 0,
    controlledOpen,
    modalOnly = false,
    onRequestClose,
    actionSource = 'ordinal_chat',
    initialSelectedEmoji,
}: OrdinalChatUpvalueControlProps) {
    const upvalueSats = useStore(state => state.upvalueSats)
    const upvalueUsd = useStore(state => state.upvalueUsd)
    const bsvUnit = useStore(state => state.bsvUnit)
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)
    const useUsd = bsvUnit === USD_UNIT
    const useSats = bsvUnit === SATOSHI_UNIT
    const signalAnswer = useMemo(
        () => answerWithSignalQuestSettings(answer, signalQuest),
        [answer, signalQuest],
    )
    const summaryAnswer = useMemo(() => {
        const sourceAnswers = answers?.length ? answers : [signalAnswer]
        return {
            ...signalAnswer,
            bsv_upvalues: sourceAnswers.flatMap(item => item.bsv_upvalues || []),
            reactions: sourceAnswers.flatMap(item => item.reactions || []),
        }
    }, [answers, signalAnswer])
    const {
        currentUser,
        handleUpvalue,
        showBoost,
        signalType,
        upvaluesEnabled,
        walletBalanceSats,
    } = useUpvalueAction({ answer: signalAnswer, source: actionSource })
    const seedingContext = useSeedingContextForAnswer(signalAnswer, signalQuest)
    const seedAvailable = seedingContext.canChooseSeed || seedingContext.forceSeed
    const minimumArmedSats = seedAvailable ? seedMinimumSats() : 1
    const [internalOpen, setInternalOpen] = useState(false)
    const open = controlledOpen ?? internalOpen
    const exchangeRate = useBsvExchangeRate()
    const defaultUsd = normalizeUsd(upvalueUsd)
    const defaultSats = Math.max(
        minimumArmedSats,
        (useUsd ? usdToSats(defaultUsd, exchangeRate) : null) ?? (upvalueSats || 10_000),
    )
    // Every sheet owns its picks, and a pick lasts one visit: nothing picked
    // here reaches another post, another surface, or the next open.
    const amountSession = useMemo(() => createOrdinalChatAmountSession(), [])
    const fallbackAmount = useMemo(
        () => ({
            sats: defaultSats,
            usd:
                useUsd && defaultUsd != null
                    ? Math.max(defaultUsd, satsToUsd(minimumArmedSats, exchangeRate) ?? defaultUsd)
                    : null,
        }),
        [defaultSats, defaultUsd, exchangeRate, minimumArmedSats, useUsd],
    )
    const sharedAmount = useSyncExternalStore(
        amountSession.subscribe,
        () => amountSession.snapshot(useUsd),
        () => null,
    )
    const localSats = Math.max(sharedAmount?.sats ?? fallbackAmount.sats, minimumArmedSats)
    const localUsd = useUsd ? sharedAmount?.usd ?? fallbackAmount.usd : null
    const setLocalSats = (value: number | ((current: number) => number)) => {
        const current = amountSession.snapshot(useUsd)
        const currentSats = current?.sats ?? localSats
        const nextSats = typeof value === 'function' ? value(currentSats) : value
        amountSession.remember(useUsd, {
            sats: nextSats,
            usd: useUsd ? current?.usd ?? localUsd : null,
        })
    }
    const setLocalUsd = (value: number | null) => {
        const current = amountSession.snapshot(useUsd)
        amountSession.remember(useUsd, {
            sats: current?.sats ?? localSats,
            usd: useUsd ? value : null,
        })
    }
    // Compare against the store, so a default saved from another row or picker
    // is reflected here too.
    const savedSats = defaultSats
    const savedUsd = defaultUsd
    const [amountOpen, setAmountOpen] = useState(false)
    const [customEditing, setCustomEditing] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<ReactionOption[]>([])
    const [searching, setSearching] = useState(false)
    const [recentEmojis, setRecentEmojis] = useState<string[]>([])
    const [selectedEmoji, setSelectedEmoji] = useState<string | null>(initialSelectedEmoji || null)
    const [showValueTray, setShowValueTray] = useState(false)
    const [desktopPicker, setDesktopPicker] = useState(false)
    const [popoverPosition, setPopoverPosition] = useState<
        ReturnType<typeof resolveOrdinalChatPopoverPosition> | undefined
    >()
    const controlRef = useRef<HTMLDivElement>(null)
    const reactionButtonRef = useRef<HTMLButtonElement>(null)
    const sheetRef = useRef<HTMLElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const valuePillRef = useRef<HTMLDivElement>(null)
    const searchRequestRef = useRef(0)
    const summary = useMemo(() => summarizeOrdinalChatSignals(summaryAnswer), [summaryAnswer])
    const recipientName = signalAnswer.user?.name || 'this creator'
    const recipientHandle = signalAnswer.user?.name ? `@${signalAnswer.user.name}` : recipientName
    const viewerSeedSats =
        summary.seeders.find(seeder => String(seeder.userId) === String(currentUser?.id))?.sats || 0
    const viewerHasSeed = ordinalChatViewerHasSeed(summary, currentUser?.id)
    const seedActionSats = Math.round(localSats)
    const projectedSeedShare = projectedOrdinalChatSeedShare({
        seedSats: summary.seedSats,
        viewerSeedSats,
        nextSeedSats: seedActionSats,
    })
    const anchoredDesktop = desktopPicker && !modalOnly

    const setOpenState = useCallback(
        (nextOpen: boolean) => {
            if (controlledOpen === undefined) setInternalOpen(nextOpen)
            onOpenChange?.(nextOpen)
            if (!nextOpen) onRequestClose?.()
        },
        [controlledOpen, onOpenChange, onRequestClose],
    )

    const wasOpenRef = useRef(false)
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setRecentEmojis(getRecentReactionEmojis())
            setSearchQuery('')
            setSearchResults([])
            setAmountOpen(false)
            setCustomEditing(false)
            setShowValueTray(false)
            if (initialSelectedEmoji !== undefined) setSelectedEmoji(initialSelectedEmoji)
        }
        // Each visit starts from the saved default and its picks end with it.
        if (open !== wasOpenRef.current) amountSession.reset()
        wasOpenRef.current = open
    }, [amountSession, initialSelectedEmoji, open])

    useEffect(() => {
        if (sharedAmount && amountSession.snapshot(useUsd) === sharedAmount) {
            setLocalSats(current => Math.max(current, minimumArmedSats))
        }
    }, [minimumArmedSats])

    useEffect(() => {
        if (!open) return
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            event.stopPropagation()
            setOpenState(false)
        }
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [open, setOpenState])

    useEffect(() => {
        if (!showValueTray) return
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            event.preventDefault()
            event.stopImmediatePropagation()
            setShowValueTray(false)
        }
        document.addEventListener('keydown', handleEscape, true)
        return () => document.removeEventListener('keydown', handleEscape, true)
    }, [showValueTray])

    useEffect(() => {
        const media = window.matchMedia('(min-width: 900px)')
        const update = () => setDesktopPicker(media.matches)
        update()
        if (media.addEventListener) media.addEventListener('change', update)
        else media.addListener(update)
        return () => {
            if (media.removeEventListener) media.removeEventListener('change', update)
            else media.removeListener(update)
        }
    }, [])

    useEffect(() => {
        if (!open || !anchoredDesktop) return
        const frame = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus()
        })
        return () => window.cancelAnimationFrame(frame)
    }, [anchoredDesktop, open])

    useLayoutEffect(() => {
        if (!open || !anchoredDesktop) {
            setPopoverPosition(undefined)
            return
        }

        const updatePosition = () => {
            const sheet = sheetRef.current
            const reactionButton = reactionButtonRef.current
            const target = controlRef.current?.closest<HTMLElement>('[data-ordinal-chat-run]')
            if (!sheet || !reactionButton || !target) return

            const sheetRect = sheet.getBoundingClientRect()
            const targetRect = target.getBoundingClientRect()
            const anchorRect = reactionButton.getBoundingClientRect()
            const nextPosition = resolveOrdinalChatPopoverPosition({
                boundary: {
                    top: 0,
                    right: document.documentElement.clientWidth,
                    bottom: document.documentElement.clientHeight,
                    left: 0,
                },
                target: targetRect,
                anchorRight: anchorRect.right,
                popoverWidth: sheetRect.width,
                popoverHeight: Math.max(sheetRect.height, sheet.scrollHeight),
            })
            setPopoverPosition(current =>
                current?.left === nextPosition.left &&
                current.top === nextPosition.top &&
                current.placement === nextPosition.placement &&
                current.maxHeight === nextPosition.maxHeight
                    ? current
                    : nextPosition,
            )
        }

        updatePosition()
        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updatePosition)
        if (sheetRef.current) resizeObserver?.observe(sheetRef.current)
        const target = controlRef.current?.closest<HTMLElement>('[data-ordinal-chat-run]')
        if (target) resizeObserver?.observe(target)
        window.addEventListener('resize', updatePosition)
        document.addEventListener('scroll', updatePosition, true)
        return () => {
            resizeObserver?.disconnect()
            window.removeEventListener('resize', updatePosition)
            document.removeEventListener('scroll', updatePosition, true)
        }
    }, [anchoredDesktop, open])

    useEffect(() => {
        const query = searchQuery.trim()
        const requestId = searchRequestRef.current + 1
        searchRequestRef.current = requestId
        if (!query) {
            setSearchResults([])
            setSearching(false)
            return
        }

        setSearching(true)
        searchReactionOptions(query)
            .then(results => {
                if (searchRequestRef.current === requestId) setSearchResults(results)
            })
            .catch(error => {
                if (searchRequestRef.current !== requestId) return
                console.error('Failed to search ordinal chat reactions', error)
                setSearchResults([])
            })
            .finally(() => {
                if (searchRequestRef.current === requestId) setSearching(false)
            })
    }, [searchQuery])

    useEffect(() => {
        // Defaults remain live fallbacks, not picks. A reset earlier in this
        // effect flush must also win over conversion from the previous visit.
        if (
            !sharedAmount ||
            amountSession.snapshot(useUsd) !== sharedAmount ||
            !useUsd ||
            localUsd == null ||
            !exchangeRate
        )
            return
        const converted = usdToSats(localUsd, exchangeRate)
        if (converted == null) return
        if (converted < minimumArmedSats) {
            setLocalSats(minimumArmedSats)
            const minimumUsd = satsToUsd(minimumArmedSats, exchangeRate)
            if (minimumUsd != null && !usdAmountsEqual(localUsd, minimumUsd)) {
                setLocalUsd(minimumUsd)
            }
            return
        }
        setLocalSats(converted)
    }, [amountSession, exchangeRate, localUsd, minimumArmedSats, sharedAmount, useUsd])

    const reactionOptions = useMemo(
        () =>
            buildOrdinalChatReactionOptions({
                recentEmojis,
                searchResults,
                searching: !!searchQuery.trim(),
                resolveOption: resolveReactionOption,
                includeDefaultUpvalue: !anchoredDesktop,
                limit: anchoredDesktop ? 32 : 24,
            }),
        [anchoredDesktop, recentEmojis, searchQuery, searchResults],
    )

    const isDefaultPreset = useCallback(
        (preset: AmountPreset) =>
            useUsd && savedUsd != null
                ? usdAmountsEqual(preset.usd, savedUsd)
                : preset.sats != null && Math.round(preset.sats) === Math.round(savedSats),
        [savedSats, savedUsd, useUsd],
    )
    const amountPresets = useMemo<AmountPreset[]>(
        () =>
            useUsd
                ? ORDINAL_CHAT_USD_PRESETS.map(preset => ({
                      label: preset.label,
                      sats: usdToSats(preset.usd, exchangeRate),
                      usd: preset.usd,
                  }))
                : ORDINAL_CHAT_SATS_PRESETS.map(preset => ({
                      label: preset.label,
                      sats: preset.sats,
                      usd: null,
                  })),
        [exchangeRate, useUsd],
    )
    // A default saved in one denomination rarely lands on a preset of the
    // other ($0.01 is ~50K sats). It then lives in the Custom cell, starred.
    const customDefault = useMemo<AmountPreset | null>(() => {
        if (amountPresets.some(isDefaultPreset)) return null
        const label = useUsd
            ? formatUsdValue(savedUsd ?? satsToUsd(savedSats, exchangeRate))
            : compactNumber.format(savedSats)
        if (!label) return null
        return {
            label,
            sats: useUsd && savedUsd != null ? usdToSats(savedUsd, exchangeRate) : savedSats,
            usd: useUsd ? savedUsd : null,
        }
    }, [amountPresets, exchangeRate, isDefaultPreset, savedSats, savedUsd, useUsd])
    const activePreset = amountPresets.find(preset =>
        useUsd
            ? usdAmountsEqual(preset.usd, localUsd)
            : preset.sats != null && preset.sats === localSats,
    )
    const usdValue = formatUsdValue(
        useUsd && localUsd != null ? localUsd : satsToUsd(localSats, exchangeRate),
    )
    const amountLabel =
        activePreset?.label ||
        (useUsd && usdValue ? usdValue : compactNumber.format(Math.max(0, localSats)))
    // The current amount is not a preset, so it is shown in the Custom cell.
    const customSelected = !activePreset
    // The quick + button always sends the saved default. An amount picked in
    // the drop-up is a one-time send for this visit's taps in the sheet; it
    // becomes the quick amount only once set as the default.
    const quickAmountLabel =
        (useUsd && formatUsdValue(satsToUsd(defaultSats, exchangeRate))) ||
        compactNumber.format(Math.max(0, defaultSats))
    const amountButtonLabel = useUsd ? amountLabel.replace(/^\$/, '') : amountLabel
    const isDefault = useUsd
        ? usdAmountsEqual(localUsd, savedUsd)
        : Math.round(localSats) === Math.round(savedSats)
    const conversionLabel = useUsd
        ? localSats > 0
            ? `≈ ${localSats.toLocaleString()} sats`
            : null
        : usdValue
        ? `≈ ${usdValue.includes('$') ? usdValue : `$${usdValue}`}`
        : null

    const selectPreset = (sats: number | null, usd: number | null) => {
        if (sats == null) return
        const nextSats = Math.max(sats, minimumArmedSats)
        // A picked preset lasts one visit, like a custom amount; only "Set as
        // default" makes an amount stick.
        setLocalSats(nextSats)
        setLocalUsd(
            useUsd && nextSats !== sats ? satsToUsd(nextSats, exchangeRate) : useUsd ? usd : null,
        )
        setCustomEditing(false)
    }

    const saveDefault = async () => {
        const options = paidSignalDefaultOptions(false, {
            sats: Math.max(minimumArmedSats, Math.round(localSats)),
            usd: useUsd ? normalizeUsd(localUsd) : null,
        })
        try {
            await api.updateUserSpaceOptions({ ...options, signalType: 'upvalue' })
            useStore.setState({ ...options, signalType: savedPaidSignalType(signalType) })
        } catch (error) {
            console.error('Failed to save ordinal chat upvalue default', error)
        }
    }

    const commitPaidAction = (isSeed: boolean, emoji?: string, amountSats = localSats) => {
        if (!upvaluesEnabled || (isSeed && !seedAvailable)) return
        const sats = Math.max(minimumArmedSats, Math.round(amountSats))
        const actionType = showBoost ? 'boost' : signalType
        const commit = () => {
            if (emoji) {
                setSelectedEmoji(emoji)
                onReactionSelected?.(emoji)
            }
            setOpenState(false)
            void handleUpvalue(sats, undefined, actionType, {
                ...(emoji ? { reactionEmoji: emoji } : {}),
                source: actionSource,
                isSeed,
            })
            onReactionCommitted?.()
        }

        if (sats > SATS_BEFORE_CONFIRM) {
            const verb = isSeed ? 'Seed' : 'Upvalue'
            useStore
                .getState()
                .actions.modalActions.openConfirmModal(
                    `${verb} ${toBitcoin(sats)} bitcoin?`,
                    verb,
                    commit,
                    {
                        modalKey: LARGE_UPVALUE_CONFIRM_MODAL_KEY,
                        disableOverlayBlur: true,
                    },
                )
            return
        }
        commit()
    }

    const openControl = () => {
        if (!currentUser) {
            openAuthModal('signup', 'ordinal_chat_upvalue')
            return
        }
        if (!upvaluesEnabled) return
        setShowValueTray(false)
        setOpenState(true)
    }

    const stopModalEvent = (event: React.SyntheticEvent) => event.stopPropagation()
    const seedHalfLabel = compactNumber.format(Math.round(seedActionSats / 2))
    const amountButton = (
        <button
            type="button"
            className={amountOpen ? styles.amountButtonOpen : styles.amountButton}
            onClick={() => setAmountOpen(value => !value)}
            aria-expanded={amountOpen}
        >
            {useUsd ? (
                <span className={styles.amountUnit}>$</span>
            ) : (
                <SatsIcon showBars={false} contextStyles={styles.bolt} inline />
            )}
            <span>{amountButtonLabel}</span>
            {amountOpen ? (
                <ChevronUp size={14} aria-hidden />
            ) : (
                <ChevronDown size={14} aria-hidden />
            )}
        </button>
    )

    const sheetLayer =
        open && typeof document !== 'undefined'
            ? createPortal(
                  <>
                      <button
                          type="button"
                          className={`${styles.backdrop} ${
                              modalOnly ? '' : styles.anchoredBackdrop
                          }`}
                          data-no-modal-interaction
                          data-story-control-exempt="true"
                          onClick={event => {
                              event.stopPropagation()
                              event.preventDefault()
                              setOpenState(false)
                          }}
                          onMouseDown={stopModalEvent}
                          onPointerDown={stopModalEvent}
                          onTouchStart={stopModalEvent}
                          aria-label="Close reactions"
                      />
                      <section
                          ref={sheetRef}
                          className={`${styles.sheet} ${modalOnly ? '' : styles.anchoredSheet}`}
                          role="dialog"
                          aria-modal="true"
                          aria-label={`Upvalue ${recipientName}`}
                          data-positioned={modalOnly || !!popoverPosition}
                          data-placement={popoverPosition?.placement}
                          data-no-modal-interaction
                          data-story-control-exempt="true"
                          style={
                              anchoredDesktop && popoverPosition
                                  ? {
                                        top: `${popoverPosition.top}px`,
                                        left: `${popoverPosition.left}px`,
                                        ...(popoverPosition.maxHeight != null
                                            ? { maxHeight: `${popoverPosition.maxHeight}px` }
                                            : {}),
                                    }
                                  : undefined
                          }
                          onClick={stopModalEvent}
                          onMouseDown={stopModalEvent}
                          onPointerDown={stopModalEvent}
                          onTouchStart={stopModalEvent}
                      >
                          <header className={styles.recipientBar}>
                              <UserIcon
                                  user={signalAnswer.user}
                                  size="small"
                                  contextStyles={styles.recipientAvatar}
                              />
                              <strong>{recipientName}</strong>
                              <button
                                  type="button"
                                  className={styles.closeButton}
                                  onClick={() => setOpenState(false)}
                                  aria-label="Close reactions"
                              >
                                  <X size={18} aria-hidden />
                              </button>
                          </header>

                          <div className={styles.armingBar}>
                              <p>
                                  {summary.seedSats > 0 ? (
                                      <>
                                          {amountButton}
                                          <span>to {recipientHandle}, shared with the seeders</span>
                                      </>
                                  ) : (
                                      <>
                                          <span>Send</span>
                                          {amountButton}
                                          <span>to {recipientHandle}</span>
                                      </>
                                  )}
                              </p>
                          </div>

                          {amountOpen && (
                              <div className={styles.amountPicker}>
                                  <div className={styles.amountHeader}>
                                      <strong>Amount</strong>
                                      {walletBalanceSats != null && (
                                          <span>
                                              Balance {compactNumber.format(walletBalanceSats)}
                                          </span>
                                      )}
                                  </div>
                                  <div className={styles.amountGrid}>
                                      {amountPresets.map(preset => (
                                          <button
                                              key={preset.label}
                                              type="button"
                                              className={
                                                  (useUsd &&
                                                      usdAmountsEqual(preset.usd, localUsd)) ||
                                                  (useSats && preset.sats === localSats)
                                                      ? styles.amountSelected
                                                      : undefined
                                              }
                                              onClick={() => selectPreset(preset.sats, preset.usd)}
                                              disabled={preset.sats == null}
                                          >
                                              {isDefaultPreset(preset) && (
                                                  <Star
                                                      className={styles.amountStar}
                                                      size={12}
                                                      fill="currentColor"
                                                      aria-hidden
                                                  />
                                              )}
                                              {preset.label}
                                          </button>
                                      ))}
                                      {customEditing ? (
                                          <label
                                              className={`${styles.customCell} ${styles.customEditing}`}
                                          >
                                              <input
                                                  type="number"
                                                  min={useUsd ? 0.001 : minimumArmedSats}
                                                  step={useUsd ? 0.001 : 1}
                                                  value={useUsd ? localUsd || '' : localSats || ''}
                                                  onChange={event => {
                                                      const value = Number(event.target.value) || 0
                                                      if (useUsd) setLocalUsd(value)
                                                      else
                                                          setLocalSats(
                                                              Math.max(value, minimumArmedSats),
                                                          )
                                                  }}
                                                  onKeyDown={event => {
                                                      if (event.key === 'Enter') {
                                                          event.preventDefault()
                                                          setCustomEditing(false)
                                                      }
                                                  }}
                                                  aria-label="Custom reaction amount"
                                                  autoFocus
                                              />
                                          </label>
                                      ) : (
                                          <button
                                              type="button"
                                              className={`${styles.customCell} ${
                                                  customSelected
                                                      ? styles.amountSelected
                                                      : customDefault
                                                      ? ''
                                                      : styles.customAmount
                                              }`}
                                              onClick={() =>
                                                  customSelected || !customDefault
                                                      ? setCustomEditing(true)
                                                      : selectPreset(
                                                            customDefault.sats,
                                                            customDefault.usd,
                                                        )
                                              }
                                              aria-label={
                                                  customSelected
                                                      ? `Edit custom amount ${amountLabel}`
                                                      : undefined
                                              }
                                          >
                                              {(customSelected ? isDefault : !!customDefault) && (
                                                  <Star
                                                      className={styles.amountStar}
                                                      size={12}
                                                      fill="currentColor"
                                                      aria-hidden
                                                  />
                                              )}
                                              {customSelected
                                                  ? amountLabel
                                                  : customDefault?.label || 'Custom'}
                                              {customSelected && (
                                                  <Pencil
                                                      className={styles.customPencil}
                                                      size={12}
                                                      aria-hidden
                                                  />
                                              )}
                                          </button>
                                      )}
                                  </div>
                                  <div className={styles.amountFooter}>
                                      {isDefault ? (
                                          <span className={styles.defaultLabel}>
                                              <Star size={13} fill="currentColor" aria-hidden />
                                              Your default
                                          </span>
                                      ) : (
                                          <button type="button" onClick={() => void saveDefault()}>
                                              <Star size={13} aria-hidden />
                                              Set {amountLabel} as default
                                          </button>
                                      )}
                                      {conversionLabel && <small>{conversionLabel}</small>}
                                  </div>
                              </div>
                          )}

                          <div className={styles.emojiRegion}>
                              <label className={styles.search}>
                                  <Search size={16} aria-hidden />
                                  <input
                                      ref={searchInputRef}
                                      type="search"
                                      value={searchQuery}
                                      onChange={event => {
                                          const nextQuery = event.target.value
                                          searchRequestRef.current += 1
                                          setSearchQuery(nextQuery)
                                          setSearchResults([])
                                          setSearching(!!nextQuery.trim())
                                      }}
                                      onKeyDown={event => {
                                          if (
                                              event.key !== 'Enter' ||
                                              searching ||
                                              !reactionOptions[0]
                                          )
                                              return
                                          event.preventDefault()
                                          commitPaidAction(false, reactionOptions[0].emoji)
                                      }}
                                      placeholder={
                                          anchoredDesktop ? 'Type to search' : 'Search all emoji'
                                      }
                                      aria-label="Search all emoji"
                                  />
                                  {searching && <Loader2 size={16} className={styles.spinner} />}
                              </label>
                              <div
                                  className={styles.emojiGrid}
                                  role="group"
                                  aria-label="Emoji reactions"
                              >
                                  {reactionOptions.map(option => (
                                      <button
                                          key={option.emoji}
                                          type="button"
                                          className={
                                              selectedEmoji === option.emoji
                                                  ? styles.emojiSelected
                                                  : undefined
                                          }
                                          onClick={() => commitPaidAction(false, option.emoji)}
                                          aria-label={option.label}
                                          title={option.label}
                                      >
                                          <UpvalueGlyph
                                              emoji={option.emoji}
                                              className={styles.reactionGlyph}
                                              size={26}
                                          />
                                      </button>
                                  ))}
                              </div>
                          </div>

                          {seedAvailable && (
                              <div className={styles.seedFooter}>
                                  <button
                                      type="button"
                                      className={styles.seedButton}
                                      onClick={() => commitPaidAction(true)}
                                  >
                                      <Sprout size={18} strokeWidth={2} aria-hidden />
                                      <span>Seed {amountLabel}</span>
                                      <small
                                          className={
                                              summary.seedSats > 0
                                                  ? styles.seedTotal
                                                  : styles.seedEmpty
                                          }
                                      >
                                          {summary.seedSats > 0
                                              ? `${compactNumber.format(summary.seedSats)} seeded`
                                              : 'not seeded yet'}
                                      </small>
                                  </button>
                                  {summary.seedSats > 0 ? (
                                      <>
                                          <div className={styles.seedShareRow}>
                                              <span>
                                                  <i
                                                      style={{
                                                          width: `${100 - projectedSeedShare}%`,
                                                      }}
                                                  />
                                                  <b style={{ width: `${projectedSeedShare}%` }} />
                                              </span>
                                              <strong>{projectedSeedShare.toFixed(1)}%</strong>
                                          </div>
                                          <p>
                                              Seeding sends {seedHalfLabel} to the author and{' '}
                                              {seedHalfLabel} to the seeders already in. Your{' '}
                                              {amountLabel} would hold{' '}
                                              {projectedSeedShare.toFixed(1)}% of everything that
                                              lands next.
                                          </p>
                                      </>
                                  ) : (
                                      <p>The first seed goes to the author.</p>
                                  )}
                              </div>
                          )}
                      </section>
                  </>,
                  document.body,
              )
            : null

    if (modalOnly) return sheetLayer

    const gradient = ordinalChatSeedGradient(summary.seedSats, summary.sats)
    const summaryUsd = formatUsdValue(satsToUsd(summary.sats, exchangeRate))
    const valueTray =
        showValueTray && typeof document !== 'undefined'
            ? createPortal(
                  <>
                      <button
                          type="button"
                          className={styles.valueBackdrop}
                          aria-label="Close value details"
                          onClick={() => setShowValueTray(false)}
                      />
                      <BsvValueSignals
                          answers={answers?.length ? answers : answer}
                          actionAnswer={answer}
                          upvalueAmountSats={defaultSats}
                          seedAmountSats={defaultSats}
                          postLockRef={valuePillRef}
                          setShowPopover={setShowValueTray}
                          elevated
                      />
                  </>,
                  document.body,
              )
            : null

    return (
        <div ref={controlRef} className={styles.control}>
            {(summary.count > 0 || summary.seedSats > 0) && (
                <div ref={valuePillRef} className={styles.valuePillAnchor}>
                    <button
                        type="button"
                        className={
                            summary.seedSats > 0 ? styles.summaryPillSeeded : styles.summaryPill
                        }
                        style={gradient ? { background: gradient } : undefined}
                        onClick={() => setShowValueTray(value => !value)}
                    >
                        {summary.seedSats > 0 && (
                            <Sprout
                                className={styles.summarySprout}
                                size={12}
                                strokeWidth={2}
                                aria-hidden
                            />
                        )}
                        <span
                            className={
                                useUsd && summaryUsd ? styles.summaryUsd : styles.summarySats
                            }
                        >
                            {useUsd && summaryUsd ? (
                                summaryUsd
                            ) : (
                                <>
                                    <SatsIcon
                                        showBars={false}
                                        contextStyles={styles.summaryBolt}
                                        inline
                                    />
                                    {compactNumber.format(summary.sats)}
                                </>
                            )}
                        </span>
                        {summary.sentiments.length > 0 && (
                            <span className={styles.summaryDivider} aria-hidden>
                                ·
                            </span>
                        )}
                        {summary.sentiments.slice(0, 3).map(sentiment => (
                            <UpvalueGlyph key={sentiment.emoji} emoji={sentiment.emoji} size={13} />
                        ))}
                    </button>
                </div>
            )}
            <span className={styles.controlSpacer} />
            <span
                className={`${styles.verbCluster} ${open ? styles.verbClusterOpen : ''}`}
                data-ordinal-chat-verbs
            >
                <button
                    type="button"
                    className={`${styles.quickSend} ${viewerHasSeed ? styles.quickSendSeed : ''}`}
                    onClick={() =>
                        commitPaidAction(
                            viewerHasSeed,
                            viewerHasSeed ? undefined : DEFAULT_UPVALUE_EMOJI,
                            defaultSats,
                        )
                    }
                    disabled={!upvaluesEnabled || (viewerHasSeed && !seedAvailable)}
                    aria-label={`${viewerHasSeed ? 'Seed' : 'Upvalue'} ${quickAmountLabel}`}
                    title={`${viewerHasSeed ? 'Seed' : 'Upvalue'} ${quickAmountLabel}`}
                >
                    {viewerHasSeed ? (
                        <Sprout size={12} strokeWidth={2.1} aria-hidden />
                    ) : (
                        <span aria-hidden>+</span>
                    )}
                    <span>{quickAmountLabel}</span>
                </button>
                <button
                    ref={reactionButtonRef}
                    type="button"
                    className={`${styles.verbButton} ${open ? styles.verbButtonOpen : ''}`}
                    onClick={openControl}
                    disabled={!upvaluesEnabled}
                    aria-label={`React to ${recipientName}`}
                >
                    <SmilePlus size={13} strokeWidth={2} aria-hidden />
                </button>
                <button
                    type="button"
                    className={styles.verbButton}
                    onClick={onReply}
                    aria-label={`Reply to ${recipientName}`}
                >
                    <Reply size={13} strokeWidth={2} aria-hidden />
                </button>
                <button
                    type="button"
                    className={branchCount > 0 ? styles.branchButton : styles.verbButton}
                    onClick={onBranch}
                    aria-label={`Open branches from ${recipientName}`}
                    disabled={!onBranch}
                >
                    <GitBranch size={13} strokeWidth={2} aria-hidden />
                    {branchCount > 0 && <span>{branchCount}</span>}
                </button>
            </span>
            {sheetLayer}
            {valueTray}
        </div>
    )
}
